import * as React from 'react'

import { Box, Button, Form, Line, Tag, Text } from '@factorialco/deprecated-design-system'
import {
  SignedInvoiceFragment,
  UpdateExpensesSignedInvoiceDocument,
} from 'generated/resources/graphql'
import moment from 'moment'
import { v4 as uuidv4 } from 'uuid'

import { observer } from 'mobx-react'

import DayPicker from 'modules/core/components/Inputs/DayPicker'
import MoneyField from 'modules/core/components/Inputs/MoneyField'
import Select from 'modules/core/components/Inputs/Select'
import TextInput from 'modules/core/components/Inputs/Text'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { dateToString, stringToDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { usePortal } from 'modules/core/lib/portalContext'
import app from 'modules/core/store/models/app'

import DropItem from 'modules/expenses/components/common/DropItem'
import { getStatusLabel } from 'modules/expenses/lib/expensables/statusLabel'
import tagColor from 'modules/expenses/lib/tagColors'
import { signedInvoiceState } from 'modules/expenses/pages/SignedInvoices/utils'
import { convertInvoiceFileToUploadFile } from 'modules/expenses/pages/SignedInvoicesDetail/utils'

import FormConfiguration from './FormConfiguration'
import TaxField, { InvoiceTax } from './TaxField'

type ExpensesSignedInvoiceTaxVat = NonNullable<SignedInvoiceFragment['taxes']>['vats'][number]

interface InvoiceFormProps {
  invoice: SignedInvoiceFragment
}

const InvoiceForm = ({ invoice }: InvoiceFormProps) => {
  const invoiceData = invoice
  const expenseData = invoice.parentExpense
  const invoiceFile = invoice.parentSignedDocument?.files[0]
  const uploadedFile = convertInvoiceFileToUploadFile(invoiceFile)
  const isReadOnly = signedInvoiceState(invoice) == 'closed'
  const InvoiceTypeEnum = {
    invoice: 'invoice',
    simplified_invoice: 'simplified_invoice',
  }
  // we should consider renaming the invoice types to document types, because they are not invoices only
  // but this also implies a change in the backend
  const invoiceTypes = [
    {
      value: InvoiceTypeEnum.invoice,
      label: i18n.t('expenses.settings.signed_invoices.detail.invoice_types.invoice'),
    },
    {
      value: InvoiceTypeEnum.simplified_invoice,
      label: i18n.t('expenses.settings.signed_invoices.detail.invoice_types.simplified'),
    },
  ]

  function parseDate(date: string, format: string) {
    if (!date) return null

    if (format === 'backend') {
      return dateToString(stringToDate(date, 'backend_datetime'), 'ui')
    } else if (format === 'ui') {
      return dateToString(stringToDate(date, 'ui'), 'backend')
    } else return date
  }
  const formConfig = useMemo(
    () =>
      new FormConfiguration({
        ...invoiceData,
        employee: expenseData?.employee?.fullName,
        irpfPercentage: invoice.taxes?.irpf?.percentage?.toString(),
        equalizationTax: invoice.taxes?.equalizationTax?.amount,
        documentDate: parseDate(invoiceData.documentDate, 'backend'),
        accountingDate: invoiceData.accountingDate
          ? parseDate(invoiceData.accountingDate, 'backend')
          : null,
        documentType:
          expenseData?.documentType === InvoiceTypeEnum.invoice
            ? InvoiceTypeEnum.invoice
            : InvoiceTypeEnum.simplified_invoice,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [invoice.id]
  )
  const taxConfig = useMemo(
    () =>
      (invoice.taxes?.vats || []).map((vat: ExpensesSignedInvoiceTaxVat) => ({
        id: uuidv4(),
        percentage: vat.percentage,
        amount: vat.amount,
        baseAmount: vat.baseAmount,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [invoice.id]
  )

  const [form, setForm] = React.useState<FormConfiguration>(formConfig)
  const [taxes, setTaxes] = React.useState<InvoiceTax[]>(taxConfig)

  const addTaxField = () => {
    setTaxes(taxes.concat({ id: uuidv4() }))
  }
  const removeTaxField = (id: string) => {
    setTaxes(taxes.filter((tax) => tax.id !== id))
  }
  const updateTaxField = (id: string, data: Record<string, number>) => {
    setTaxes(taxes.map((tax) => (tax.id === id ? { ...tax, ...data } : tax)))
  }

  useEffect(() => {
    setForm(formConfig)
    setTaxes(taxConfig)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice.id])

  const { closePortal } = usePortal()

  const [updateSignedInvoice, { loading }] = useMutation(UpdateExpensesSignedInvoiceDocument, {
    extractorFn: (data) => data.expensesMutations.updateExpensesSignedInvoice,
  })

  const onSubmit = async () => {
    const {
      irpfPercentage,
      emitterTin,
      emitterName,
      receiverName,
      receiverTin,
      discountAmount,
      equalizationTax,
      invoiceType,
      number,
      documentDate,
      accountingDate,
    } = form.data()

    const attributes = Object.fromEntries(
      Object.entries({
        emitterTin,
        emitterName,
        receiverTin,
        receiverName,
        discountAmount,
        invoiceType,
        number,
        documentDate: parseDate(documentDate, 'ui'),
        accountingDate: parseDate(accountingDate, 'ui'),
        signedForOrganization: 'aeat',
      })
        .filter(([_, value]) => value !== null)
        .map(([k, value]) => {
          return [k, value === null ? '' : value]
        })
    )

    const result = await updateSignedInvoice({
      variables: {
        id: invoice.id,
        accountingDate: attributes.accountingDate,
        discountAmount: attributes.discountAmount,
        documentDate: attributes.documentDate,
        emitterName: attributes.emitterName,
        emitterTin: attributes.emitterTin,
        invoiceType: attributes.invoiceType,
        number: attributes.number,
        receiverLegalEntityId: invoice.legalEntity?.id ?? '0',
        receiverName: attributes.receiverName,
        receiverTin: attributes.receiverTin,
        signedDocumentId: invoice.parentSignedDocument?.id,
        signedForOrganization: 'aeat',
        taxes: {
          vats: taxes.map((tax) => ({
            percentage: Number(tax.percentage),
            amount: Number(tax.amount),
            baseAmount: Number(tax.baseAmount),
          })),
          ...(irpfPercentage !== null && { irpf: { percentage: Number(irpfPercentage) } }),
          ...(equalizationTax !== null && { equalizationTax: { amount: Number(equalizationTax) } }),
        },
      },
    })

    const handled = handleResult(result, (response) => {
      if (response.messages) {
        app.setFlash({
          type: 'wrong',
          message: response.messages,
        })
      }
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('expenses.settings.signed_invoices.detail.form.actions.update'),
      })

      closePortal()
    }
  }

  return (
    <>
      <Box
        flexDirection="column"
        alignItems="center"
        justifyContent="flexStart"
        gap="s16"
        paddingY="s48"
        paddingX="s40"
        height="full"
        overflowY="auto"
      >
        <Form padding={null}>
          <Box
            flexDirection="column"
            justifyContent="flexStart"
            alignItems="flexStart"
            paddingY="s16"
            gap="s16"
          >
            <Text.Header2 color="grey1000">
              {i18n.t('expenses.settings.signed_invoices.detail.form.title')}
            </Text.Header2>
            <Line />

            <Box
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '24px',
                width: '100%',
              }}
            >
              <Box flexDirection="column" gap="s6">
                <Text weight="medium" size="200" color="grey1000">
                  {i18n.t('expenses.settings.signed_invoices.detail.form.fields.status')}
                </Text>
                <Tag
                  leftItem="circle"
                  showCircle
                  type={tagColor(invoice.parentExpense?.status)}
                  inline
                >
                  {invoice.parentExpense?.status
                    ? getStatusLabel(invoice.parentExpense?.status)
                    : ''}
                </Tag>
              </Box>

              {!!expenseData?.reviewRequestAt && (
                <Box flexDirection="column" gap="s6">
                  <Text weight="medium" size="200" color="grey1000">
                    {i18n.t('expenses.settings.signed_invoices.detail.form.fields.report_date')}
                  </Text>
                  <Tag type={tagColor('')} inline>
                    {parseDate(expenseData?.reviewRequestAt, 'backend')}
                  </Tag>
                </Box>
              )}
            </Box>
          </Box>

          <TextInput
            readonly
            label={i18n.t('expenses.settings.signed_invoices.detail.form.fields.employee')}
            field={form.get('employee')}
          />
          <DayPicker
            mandatory
            placeholder="dd/mm/yyyy"
            field={form.get('accountingDate')}
            label={i18n.t('expenses.settings.signed_invoices.detail.form.fields.accounting_date')}
            maxDate={moment().endOf('day')}
            readonly={isReadOnly}
          />
          <TextInput
            mandatory
            label={i18n.t('expenses.settings.signed_invoices.detail.form.fields.emitter_name')}
            field={form.get('emitterName')}
            readonly={isReadOnly}
          />
          <TextInput
            mandatory
            label={i18n.t('expenses.settings.signed_invoices.detail.form.fields.emitter_tin')}
            field={form.get('emitterTin')}
            readonly={isReadOnly}
          />
          <TextInput
            mandatory
            label={i18n.t('expenses.settings.signed_invoices.detail.form.fields.receiver_name')}
            field={form.get('receiverName')}
            readonly={isReadOnly}
          />
          <TextInput
            mandatory
            label={i18n.t('expenses.settings.signed_invoices.detail.form.fields.receiver_tin')}
            field={form.get('receiverTin')}
            readonly={isReadOnly}
          />

          <Box flexDirection="row" gap="s16" alignItems="flexStart">
            <Select
              mandatory
              label={i18n.t('expenses.settings.signed_invoices.detail.form.fields.invoice_type')}
              field={form.get('invoiceType')}
              readonly={isReadOnly}
              options={invoiceTypes}
            />
            <TextInput
              mandatory
              label={i18n.t('expenses.settings.signed_invoices.detail.form.fields.number')}
              field={form.get('number')}
              readonly={isReadOnly}
            />
          </Box>

          <Box flexDirection="row" gap="s16" alignItems="flexStart">
            <TextInput
              readonly
              label={i18n.t('expenses.settings.signed_invoices.detail.form.fields.currency')}
              field={form.get('currency')}
            />
            <MoneyField
              readonly
              label={i18n.t('expenses.settings.signed_invoices.detail.form.fields.amount')}
              field={form.get('amount')}
              currency={invoiceData.currency}
            />
          </Box>

          <Box gap="s16" flexDirection="column" alignItems="flexStart">
            {taxes.map((tax) => (
              <TaxField
                key={String(tax.id)}
                id={String(tax.id)}
                tax={tax}
                currency={invoiceData.currency}
                onChange={updateTaxField}
                onRemove={removeTaxField}
                isReadonly={isReadOnly}
              />
            ))}
            <Box
              paddingY="s12"
              border={{ top: { width: 's1', style: 'solid', color: 'grey400' } }}
              width="full"
            >
              {taxes.length < 5 && !isReadOnly && (
                <Button onClick={addTaxField} type="secondary" iconName="add">
                  {i18n.t('expenses.settings.signed_invoices.detail.form.buttons.add_vat')}
                </Button>
              )}
            </Box>
          </Box>

          <Box flexDirection="row" gap="s16" alignItems="flexStart">
            <MoneyField
              mandatory
              placeholder="0"
              label={i18n.t(
                'expenses.settings.signed_invoices.detail.form.fields.equalization_tax'
              )}
              field={form.get('equalizationTax')}
              currency={invoiceData.currency}
              readonly={isReadOnly}
            />
            <MoneyField
              mandatory
              placeholder="0"
              label={i18n.t('expenses.settings.signed_invoices.detail.form.fields.irpf_percentage')}
              field={form.get('irpfPercentage')}
              currency={'%'}
              readonly={isReadOnly}
            />
          </Box>

          <Box flexDirection="row" gap="s16" alignItems="flexStart">
            <MoneyField
              mandatory
              placeholder="0"
              label={i18n.t('expenses.settings.signed_invoices.detail.form.fields.discount_amount')}
              field={form.get('discountAmount')}
              currency={invoiceData.currency}
              readonly={isReadOnly}
            />
            <DayPicker
              mandatory
              placeholder="dd/mm/yyyy"
              field={form.get('documentDate')}
              label={i18n.t('expenses.settings.signed_invoices.detail.form.fields.document_date')}
              maxDate={moment().endOf('day')}
              readonly={isReadOnly}
            />
          </Box>

          <Box paddingTop="s4" paddingBottom="s4">
            <Line />
          </Box>

          {uploadedFile && (
            <DropItem
              key={invoiceFile?.id}
              file={uploadedFile}
              date={invoiceFile?.createdAt}
              showInViewer={false}
              signedDocument
            />
          )}
        </Form>
      </Box>

      {!isReadOnly && (
        <Box
          border={{ top: { width: 's1', style: 'solid', color: 'grey400' } }}
          background="grey100"
          flexDirection="row"
          justifyContent="flexEnd"
          alignItems="center"
          paddingTop="s16"
          paddingBottom="s24"
          paddingX="s40"
          gap="s8"
        >
          <Button type="primary" onClick={onSubmit} disabled={loading}>
            {i18n.t('expenses.settings.signed_invoices.detail.form.buttons.save')}
          </Button>
        </Box>
      )}
    </>
  )
}

export default observer(InvoiceForm)
